import React, { useState, useCallback, useMemo, memo, useEffect, ReactNode } from 'react';

// Type definitions
interface HelmetProps {
  children: React.ReactElement;
}

interface AnalyticsEvent {
  event: string;
  properties: Record<string, any>;
  timestamp: string;
}

interface ConversionOptions {
  outputFormat: OutputFormat;
  tableDetection: TableDetectionMode;
  pageRange: string;
  includeImages: boolean;
  includeTextBoxes: boolean;
  preserveFormatting: boolean;
  mergeSmallCells: boolean;
  autoFitColumns: boolean;
  headerRows: number;
  skipEmptyRows: boolean;
  skipEmptyColumns: boolean;
  dateFormat: DateFormat;
  numberFormat: NumberFormat;
  encoding: string;
  sheetNaming: SheetNaming;
  imageHandling: ImageHandling;
  qualityLevel: QualityLevel;
  ocrEnabled: boolean;
  ocrLanguage: string;
  removeWatermarks: boolean;
  rotationAngle: number;
  contrast: number;
  brightness: number;
  cleanupText: boolean;
  customDelimiter: string;
  thousandsSeparator: string;
  decimalSeparator: string;
  passwordProtection: boolean;
  password: string;
  compressionLevel: CompressionLevel;
}

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

interface AnalyticsHook {
  track: (event: string, properties?: Record<string, any>) => void;
}

interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterCard: string;
}

interface AnalyticsStats {
  total: number;
  conversions: number;
  validations: number;
}

interface PDFInfo {
  fileName: string;
  fileSize: number;
  pageCount: number;
  version: string;
  hasImages: boolean;
  hasTables: boolean;
  isPasswordProtected: boolean;
  metadata: {
    title?: string;
    author?: string;
    subject?: string;
    creator?: string;
    producer?: string;
    creationDate?: string;
    modificationDate?: string;
  };
}

// Output format options
type OutputFormat = 'xlsx' | 'xls' | 'csv' | 'ods' | 'tsv';
type TableDetectionMode = 'automatic' | 'manual' | 'ai-enhanced' | 'grid-based' | 'text-based';
type DateFormat = 'auto' | 'mm/dd/yyyy' | 'dd/mm/yyyy' | 'yyyy-mm-dd' | 'custom';
type NumberFormat = 'auto' | 'general' | 'currency' | 'percentage' | 'scientific' | 'custom';
type SheetNaming = 'page-numbers' | 'table-headers' | 'custom' | 'sequential';
type ImageHandling = 'ignore' | 'embed' | 'extract' | 'link';
type QualityLevel = 'fast' | 'balanced' | 'high' | 'ultra';
type CompressionLevel = 'none' | 'low' | 'medium' | 'high' | 'maximum';

// Mock react-helmet-async for SEO
const Helmet: React.FC<HelmetProps> = ({ children }) => {
  useEffect(() => {
    const extractPropsFromChildren = (element: React.ReactElement): any => {
      if (element.props?.children) {
        const childArray = React.Children.toArray(element.props.children);
        const helmetData: any = { meta: [], link: [] };

        childArray.forEach((child: any) => {
          if (React.isValidElement(child)) {
            const childElement = child as React.ReactElement<any>;

            if (childElement.type === 'title') {
              helmetData.title = childElement.props.children;
            } else if (childElement.type === 'meta') {
              helmetData.meta.push(childElement.props);
            } else if (childElement.type === 'link') {
              helmetData.link.push(childElement.props);
            } else if (childElement.type === 'script') {
              if (childElement.props.type === 'application/ld+json') {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.textContent = childElement.props.children;
                document.head.appendChild(script);
              }
            }
          }
        });
        return helmetData;
      }
      return {};
    };

    const helmetData = extractPropsFromChildren(children);

    if (helmetData.title) {
      document.title = helmetData.title;
    }

    const updateMeta = (name: string, content: string): void => {
      let meta = document.querySelector(`meta[name="${name}"]`) ||
        document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (helmetData.meta && Array.isArray(helmetData.meta)) {
      helmetData.meta.forEach((meta: any) => {
        if (meta.name) updateMeta(meta.name, meta.content);
        if (meta.property) updateMeta(meta.property, meta.content);
      });
    }

    if (helmetData.link && Array.isArray(helmetData.link)) {
      helmetData.link.forEach((link: any) => {
        let linkEl = document.querySelector(`link[rel="${link.rel}"]`) as HTMLLinkElement;
        if (!linkEl) {
          linkEl = document.createElement('link') as HTMLLinkElement;
          linkEl.rel = link.rel;
          document.head.appendChild(linkEl);
        }
        Object.keys(link).forEach((attr) => {
          if (attr !== 'rel') linkEl.setAttribute(attr, link[attr]);
        });
      });
    }
  }, [children]);

  return null;
};

// Analytics hook with in-memory storage
const useAnalytics = (): AnalyticsHook => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsEvent[]>([]);

  const track = useCallback((event: string, properties: Record<string, any> = {}): void => {
    console.log('Analytics Event:', event, properties);

    const analyticsEvent: AnalyticsEvent = {
      event,
      properties,
      timestamp: new Date().toISOString()
    };

    setAnalyticsData(prev => [...prev.slice(-99), analyticsEvent]);

    // Also track with gtag if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event, properties);
    }
  }, []);

  return { track };
};

// Theme context
const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('theme');
      return stored === 'dark' ||
        (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  const toggleTheme = useCallback((): void => {
    setIsDark((prev) => {
      const newTheme = !prev;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('theme', newTheme ? 'dark' : 'light');
      }
      return newTheme;
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Mock PDF analysis function
const analyzePDFFile = async (file: File): Promise<PDFInfo> => {
  // Simulate PDF analysis
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    fileName: file.name,
    fileSize: file.size,
    pageCount: Math.floor(Math.random() * 50) + 1,
    version: '1.4',
    hasImages: Math.random() > 0.5,
    hasTables: Math.random() > 0.3,
    isPasswordProtected: Math.random() > 0.8,
    metadata: {
      title: 'Sample PDF Document',
      author: 'Document Author',
      subject: 'PDF Analysis',
      creator: 'PDF Creator',
      producer: 'PDF Producer',
      creationDate: '2024-01-01',
      modificationDate: new Date().toISOString().split('T')[0]
    }
  };
};

// Mock PDF to Excel conversion
const convertPDFToExcel = async (file: File, options: ConversionOptions): Promise<Blob> => {
  // Simulate conversion process
  await new Promise(resolve => setTimeout(resolve, 3000));
  
  // Create mock Excel data
  const mockExcelData = `Name,Age,Department,Salary,Start Date
John Doe,30,Engineering,75000,2022-01-15
Jane Smith,28,Marketing,65000,2022-03-10
Mike Johnson,35,Sales,70000,2021-11-20
Sarah Wilson,32,HR,60000,2023-02-01
David Brown,29,IT,72000,2022-08-05`;

  return new Blob([mockExcelData], { type: 'text/csv' });
};

// Analytics Dashboard Component
const AnalyticsDashboard: React.FC = memo(() => {
  const [analytics, setAnalytics] = useState<AnalyticsEvent[]>([]);
  const { isDark } = useTheme();

  const stats: AnalyticsStats = useMemo(() => {
    const total = analytics.length;
    const conversions = analytics.filter((a) => a.event === 'pdf_converted').length;
    const validations = analytics.filter((a) => a.event === 'pdf_analyzed').length;

    return { total, conversions, validations };
  }, [analytics]);

  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Usage Analytics
      </h3>
      <div className="grid grid-cols-4 gap-4">
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            {Math.floor(Math.random() * 100) + 50}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Total Files
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
            {Math.floor(Math.random() * 50) + 20}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Conversions
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
            {Math.floor(Math.random() * 30) + 10}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Success Rate
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
            {Math.floor(Math.random() * 1000) + 500}MB
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Data Processed
          </div>
        </div>
      </div>
    </div>
  );
});

AnalyticsDashboard.displayName = 'AnalyticsDashboard';

// PDF Preview Component
const PDFPreview: React.FC<{ pdfInfo: PDFInfo | null; isDark: boolean }> = memo(({ pdfInfo, isDark }) => {
  if (!pdfInfo) return null;

  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        PDF Analysis
      </h3>
      
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>File Name:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.fileName}</p>
          </div>
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>File Size:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
              {(pdfInfo.fileSize / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Pages:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.pageCount}</p>
          </div>
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>PDF Version:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.version}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasTables ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasTables ? 'Has Tables' : 'No Tables'}
            </span>
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasImages ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasImages ? 'Has Images' : 'No Images'}
            </span>
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.isPasswordProtected ? 'bg-orange-500' : 'bg-green-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.isPasswordProtected ? 'Protected' : 'Open'}
            </span>
          </div>
        </div>

        {pdfInfo.metadata && (
          <div className={`mt-4 p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
            <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Metadata</h4>
            <div className="space-y-1 text-sm">
              {pdfInfo.metadata.title && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Title: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.title}</span>
                </div>
              )}
              {pdfInfo.metadata.author && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Author: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.author}</span>
                </div>
              )}
              {pdfInfo.metadata.creationDate && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Created: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.creationDate}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

PDFPreview.displayName = 'PDFPreview';

// Main Component Content
const PDFToExcelContent: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { track } = useAnalytics();

  // States
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pdfInfo, setPdfInfo] = useState<PDFInfo | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [conversionProgress, setConversionProgress] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [fileName, setFileName] = useState<string>('converted_data');
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [dragActive, setDragActive] = useState<boolean>(false);

  // Advanced options
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('xlsx');
  const [tableDetection, setTableDetection] = useState<TableDetectionMode>('automatic');
  const [pageRange, setPageRange] = useState<string>('all');
  const [includeImages, setIncludeImages] = useState<boolean>(false);
  const [includeTextBoxes, setIncludeTextBoxes] = useState<boolean>(true);
  const [preserveFormatting, setPreserveFormatting] = useState<boolean>(true);
  const [mergeSmallCells, setMergeSmallCells] = useState<boolean>(true);
  const [autoFitColumns, setAutoFitColumns] = useState<boolean>(true);
  const [headerRows, setHeaderRows] = useState<number>(1);
  const [skipEmptyRows, setSkipEmptyRows] = useState<boolean>(true);
  const [skipEmptyColumns, setSkipEmptyColumns] = useState<boolean>(true);
  const [dateFormat, setDateFormat] = useState<DateFormat>('auto');
  const [numberFormat, setNumberFormat] = useState<NumberFormat>('auto');
  const [encoding, setEncoding] = useState<string>('UTF-8');
  const [sheetNaming, setSheetNaming] = useState<SheetNaming>('page-numbers');
  const [imageHandling, setImageHandling] = useState<ImageHandling>('ignore');
  const [qualityLevel, setQualityLevel] = useState<QualityLevel>('balanced');
  const [ocrEnabled, setOcrEnabled] = useState<boolean>(false);
  const [ocrLanguage, setOcrLanguage] = useState<string>('eng');
  const [removeWatermarks, setRemoveWatermarks] = useState<boolean>(false);
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [contrast, setContrast] = useState<number>(100);
  const [brightness, setBrightness] = useState<number>(100);
  const [cleanupText, setCleanupText] = useState<boolean>(true);
  const [customDelimiter, setCustomDelimiter] = useState<string>(',');
  const [thousandsSeparator, setThousandsSeparator] = useState<string>(',');
  const [decimalSeparator, setDecimalSeparator] = useState<string>('.');
  const [passwordProtection, setPasswordProtection] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [compressionLevel, setCompressionLevel] = useState<CompressionLevel>('medium');

  // SEO data
  const seoData: SEOData = useMemo(() => ({
    title: "Free PDF to Excel Converter Online - Convert PDF Tables to Excel Format",
    description: "Convert PDF files to Excel spreadsheets online for free. Advanced features include table detection, OCR, image extraction, and format preservation. Perfect for data analysis and reporting.",
    keywords: "PDF to Excel converter, PDF converter, Excel converter, online converter, free PDF converter, PDF parser, Excel formatter, data extraction, table extraction",
    canonical: "https://yoursite.com/pdf-to-excel-converter",
    ogTitle: "PDF to Excel Converter - Free Online Tool",
    ogDescription: "Convert your PDF files to Excel format instantly. Supports table detection, OCR, and advanced formatting options for perfect data extraction.",
    ogImage: "https://yoursite.com/og-pdf-excel-converter.jpg",
    twitterCard: "summary_large_image"
  }), []);

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File): Promise<void> => {
    if (!file.type.includes('pdf')) {
      setError('Please select a valid PDF file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      setError('File size must be less than 100MB');
      return;
    }

    setSelectedFile(file);
    setError('');
    setSuccess('');
    setPdfInfo(null);
    
    // Set default filename
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop();
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }

    track('pdf_file_selected', {
      fileName: file.name,
      fileSize: file.size
    });

    // Analyze PDF
    setIsAnalyzing(true);
    try {
      const info = await analyzePDFFile(file);
      setPdfInfo(info);
      track('pdf_analyzed', {
        pageCount: info.pageCount,
        hasImages: info.hasImages,
        hasTables: info.hasTables,
        isPasswordProtected: info.isPasswordProtected
      });
    } catch (err) {
      setError(`Failed to analyze PDF: ${err instanceof Error ? err.message : String(err)}`);
      track('pdf_analysis_error', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsAnalyzing(false);
    }
  }, [track]);

  // Handle file upload
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Handle drag and drop
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  // Convert PDF to Excel
  const convertToExcel = useCallback(async (): Promise<void> => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setError('');
    setSuccess('');
    setIsConverting(true);
    setConversionProgress(0);

    const options: ConversionOptions = {
      outputFormat,
      tableDetection,
      pageRange,
      includeImages,
      includeTextBoxes,
      preserveFormatting,
      mergeSmallCells,
      autoFitColumns,
      headerRows,
      skipEmptyRows,
      skipEmptyColumns,
      dateFormat,
      numberFormat,
      encoding,
      sheetNaming,
      imageHandling,
      qualityLevel,
      ocrEnabled,
      ocrLanguage,
      removeWatermarks,
      rotationAngle,
      contrast,
      brightness,
      cleanupText,
      customDelimiter,
      thousandsSeparator,
      decimalSeparator,
      passwordProtection,
      password,
      compressionLevel
    };

    try {
      const startTime = Date.now();

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setConversionProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 200);

      const resultBlob = await convertPDFToExcel(selectedFile, options);
      
      clearInterval(progressInterval);
      setConversionProgress(100);

      // Download the result
      const url = URL.createObjectURL(resultBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName || 'converted_data'}.${outputFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const processingTime = Date.now() - startTime;
      setSuccess('PDF successfully converted to Excel! Download started automatically.');
      
      track('pdf_converted', {
        format: outputFormat,
        processingTime,
        fileSize: selectedFile.size,
        pageCount: pdfInfo?.pageCount,
        options: {
          tableDetection,
          qualityLevel,
          ocrEnabled,
          includeImages
        }
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Conversion failed: ${errorMessage}`);
      track('conversion_error', { error: errorMessage });
    } finally {
      setIsConverting(false);
      setConversionProgress(0);
    }
  }, [selectedFile, fileName, outputFormat, tableDetection, pageRange, includeImages, includeTextBoxes, preserveFormatting, mergeSmallCells, autoFitColumns, headerRows, skipEmptyRows, skipEmptyColumns, dateFormat, numberFormat, encoding, sheetNaming, imageHandling, qualityLevel, ocrEnabled, ocrLanguage, removeWatermarks, rotationAngle, contrast, brightness, cleanupText, customDelimiter, thousandsSeparator, decimalSeparator, passwordProtection, password, compressionLevel, pdfInfo?.pageCount, track]);

  // Clear all
  const clearAll = useCallback((): void => {
    setSelectedFile(null);
    setPdfInfo(null);
    setError('');
    setSuccess('');
    setFileName('converted_data');
    setConversionProgress(0);
    track('data_cleared');
  }, [track]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback((): void => {
    setIsFullscreen(!isFullscreen);
    track('fullscreen_toggled', { isFullscreen: !isFullscreen });
  }, [isFullscreen, track]);

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900' : 'bg-gray-50'} ${isFullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''}`}>

      {/* SEO Meta Tags */}
      <Helmet>
        <>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <meta name="keywords" content={seoData.keywords} />
          <link rel="canonical" href={seoData.canonical} />

          <meta property="og:title" content={seoData.ogTitle} />
          <meta property="og:description" content={seoData.ogDescription} />
          <meta property="og:image" content={seoData.ogImage} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={seoData.canonical} />

          <meta name="twitter:card" content={seoData.twitterCard} />
          <meta name="twitter:title" content={seoData.ogTitle} />
          <meta name="twitter:description" content={seoData.ogDescription} />
          <meta name="twitter:image" content={seoData.ogImage} />

          <meta name="robots" content="index, follow" />
          <meta name="author" content="PDF to Excel Converter Tool" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "PDF to Excel Converter",
              "description": seoData.description,
              "url": seoData.canonical,
              "applicationCategory": "UtilityApplication",
              "operatingSystem": "Any",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            })}
          </script>
        </>
      </Helmet>

      <div className={`${isFullscreen ? 'p-4' : 'max-w-7xl mx-auto p-6'}`}>

        {/* Header with Controls */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              PDF to Excel Converter
            </h1>
            <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Convert PDF files to Excel spreadsheets with advanced table detection, OCR, and formatting options
            </p>
          </div>

          <div className="flex space-x-2">
            {/* Analytics Toggle */}
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Analytics"
              type="button"
            >
              📊
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Theme"
              type="button"
            >
              {isDark ? '☀️' : '🌙'}
            </button>

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Fullscreen"
              type="button"
            >
              {isFullscreen ? '📱' : '🖥️'}
            </button>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <div className="mb-6">
            <AnalyticsDashboard />
          </div>
        )}

        {/* Description */}
        <div className={`mb-6 p-4 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-800 text-blue-200' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
          <p>
            Convert PDF documents to Excel spreadsheets with intelligent table detection, OCR support, and advanced formatting preservation.
            Perfect for data extraction, financial reports, invoices, and document analysis.
          </p>
        </div>

        <div className={`grid ${isFullscreen ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 md:grid-cols-3'} gap-6`}>

          {/* File Upload Section */}
          <div className="flex flex-col h-full">
            <div className={`p-4 rounded-lg shadow-sm mb-4 flex-grow border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-center mb-3">
                <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  PDF File Upload
                </label>
                {selectedFile && (
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={clearAll}
                    type="button"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Drag and Drop Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? isDark ? 'border-blue-400 bg-blue-900/20' : 'border-blue-400 bg-blue-50'
                    : isDark ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="space-y-4">
                  <div className={`text-4xl ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    📄
                  </div>
                  <div>
                    <p className={`text-lg font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      Drop your PDF file here
                    </p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      or click to browse
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label
                    htmlFor="pdf-upload"
                    className={`inline-block px-4 py-2 rounded-lg font-medium cursor-pointer transition-colors ${
                      isDark
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    Select PDF File
                  </label>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Maximum file size: 100MB
                  </p>
                </div>
              </div>

              {/* File Info */}
              {selectedFile && (
                <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Selected File</h4>
                  <div className="space-y-1 text-sm">
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Name: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedFile.name}</span>
                    </div>
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Size: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Type: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedFile.type}</span>
                    </div>
                  </div>
                  
                  {isAnalyzing && (
                    <div className="mt-3 flex items-center">
                      <svg className="animate-spin h-4 w-4 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Analyzing PDF...</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* PDF Preview */}
            {pdfInfo && <PDFPreview pdfInfo={pdfInfo} isDark={isDark} />}
          </div>

          {/* Configuration Section */}
          <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Conversion Settings
            </h3>

            <div className="space-y-4">
              {/* Basic Settings */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Output File Name
                </label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={fileName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFileName(e.target.value)}
                  placeholder="Enter file name (without extension)"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Output Format
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={outputFormat}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOutputFormat(e.target.value as OutputFormat)}
                >
                  <option value="xlsx">Excel (.xlsx)</option>
                  <option value="xls">Excel Legacy (.xls)</option>
                  <option value="csv">CSV (.csv)</option>
                  <option value="ods">OpenDocument (.ods)</option>
                  <option value="tsv">Tab-Separated (.tsv)</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Table Detection Mode
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={tableDetection}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTableDetection(e.target.value as TableDetectionMode)}
                >
                  <option value="automatic">Automatic Detection</option>
                  <option value="ai-enhanced">AI-Enhanced Detection</option>
                  <option value="grid-based">Grid-Based Detection</option>
                  <option value="text-based">Text-Based Detection</option>
                  <option value="manual">Manual Selection</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Page Range
                </label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={pageRange}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPageRange(e.target.value)}
                  placeholder="e.g., 1-5, 8, 10-12 or 'all'"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Quality Level
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={qualityLevel}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setQualityLevel(e.target.value as QualityLevel)}
                >
                  <option value="fast">Fast (Quick processing)</option>
                  <option value="balanced">Balanced (Recommended)</option>
                  <option value="high">High (Better accuracy)</option>
                  <option value="ultra">Ultra (Maximum quality)</option>
                </select>
              </div>

              {/* Advanced Options Toggle */}
              <div className="mb-4">
                <button
                  className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  type="button"
                >
                  <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Advanced Settings */}
              {showAdvanced && (
                <div className={`space-y-4 p-3 rounded-lg mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  
                  {/* Data Processing Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Data Processing</h4>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="preserveFormatting"
                        checked={preserveFormatting}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPreserveFormatting(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="preserveFormatting" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Preserve formatting (fonts, colors, styles)
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="mergeSmallCells"
                        checked={mergeSmallCells}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMergeSmallCells(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="mergeSmallCells" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Merge small cells automatically
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="autoFitColumns"
                        checked={autoFitColumns}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAutoFitColumns(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="autoFitColumns" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Auto-fit column widths
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="skipEmptyRows"
                        checked={skipEmptyRows}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSkipEmptyRows(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="skipEmptyRows" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Skip empty rows
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="skipEmptyColumns"
                        checked={skipEmptyColumns}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSkipEmptyColumns(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="skipEmptyColumns" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Skip empty columns
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="cleanupText"
                        checked={cleanupText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCleanupText(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="cleanupText" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Clean up extracted text
                      </label>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Header Rows
                      </label>
                      <select
                        // type="number"
                        // min="0"
                        // max="10"
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={dateFormat}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDateFormat(e.target.value as DateFormat)}
                      >
                        <option value="auto">Auto-detect</option>
                        <option value="mm/dd/yyyy">MM/DD/YYYY</option>
                        <option value="dd/mm/yyyy">DD/MM/YYYY</option>
                        <option value="yyyy-mm-dd">YYYY-MM-DD</option>
                        <option value="custom">Custom format</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Number Format
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={numberFormat}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNumberFormat(e.target.value as NumberFormat)}
                      >
                        <option value="auto">Auto-detect</option>
                        <option value="general">General</option>
                        <option value="currency">Currency</option>
                        <option value="percentage">Percentage</option>
                        <option value="scientific">Scientific</option>
                        <option value="custom">Custom format</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Thousands Separator
                      </label>
                      <input
                        type="text"
                        maxLength={1}
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={thousandsSeparator}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setThousandsSeparator(e.target.value)}
                        placeholder=","
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Decimal Separator
                      </label>
                      <input
                        type="text"
                        maxLength={1}
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={decimalSeparator}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDecimalSeparator(e.target.value)}
                        placeholder="."
                      />
                    </div>
                  </div>

                  {/* Sheet Organization */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Sheet Organization</h4>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Sheet Naming Convention
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={sheetNaming}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSheetNaming(e.target.value as SheetNaming)}
                      >
                        <option value="page-numbers">Page Numbers (Page1, Page2...)</option>
                        <option value="table-headers">Table Headers</option>
                        <option value="sequential">Sequential (Sheet1, Sheet2...)</option>
                        <option value="custom">Custom naming</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeTextBoxes"
                        checked={includeTextBoxes}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeTextBoxes(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeTextBoxes" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include text boxes and annotations
                      </label>
                    </div>
                  </div>

                  {/* Security & Compression */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Security & Compression</h4>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="passwordProtection"
                        checked={passwordProtection}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPasswordProtection(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="passwordProtection" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Password protect output file
                      </label>
                    </div>

                    {passwordProtection && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Password
                        </label>
                        <input
                          type="password"
                          className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                          value={password}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                          placeholder="Enter password"
                        />
                      </div>
                    )}

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Compression Level
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={compressionLevel}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCompressionLevel(e.target.value as CompressionLevel)}
                      >
                        <option value="none">No compression</option>
                        <option value="low">Low compression</option>
                        <option value="medium">Medium compression</option>
                        <option value="high">High compression</option>
                        <option value="maximum">Maximum compression</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Character Encoding
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={encoding}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEncoding(e.target.value)}
                      >
                        <option value="UTF-8">UTF-8 (Recommended)</option>
                        <option value="UTF-16">UTF-16</option>
                        <option value="ASCII">ASCII</option>
                        <option value="ISO-8859-1">ISO-8859-1</option>
                        <option value="Windows-1252">Windows-1252</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Convert Button */}
              <div className="mt-6">
                <button
                  className={`w-full px-4 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    selectedFile && !isConverting
                      ? isDark
                        ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                        : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                      : isDark
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  onClick={convertToExcel}
                  disabled={!selectedFile || isConverting}
                  type="button"
                >
                  {isConverting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Converting... {Math.round(conversionProgress)}%
                    </div>
                  ) : (
                    `Convert to ${outputFormat.toUpperCase()}`
                  )}
                </button>

                {isConverting && (
                  <div className="mt-2">
                    <div className={`w-full bg-gray-200 rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${conversionProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Conversion Results
            </h3>

            {/* Status Messages */}
            {error && (
              <div className={`border p-3 rounded-lg mb-4 ${isDark ? 'bg-red-900/20 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}

            {success && (
              <div className={`border p-3 rounded-lg mb-4 ${isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'}`}>
                <div className="flex items-center mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="font-medium">Success!</p>
                </div>
                <p>{success}</p>
              </div>
            )}

            {!selectedFile && !error && !success && (
              <div className={`border p-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>
                    Upload a PDF file to start conversion
                  </p>
                </div>
              </div>
            )}

            {selectedFile && !error && !success && !isConverting && (
              <div className={`border p-6 rounded-lg ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div className="text-center">
                  <div className={`text-4xl mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    📊
                  </div>
                  <p className={`text-lg font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Ready to Convert
                  </p>
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Click the convert button to transform your PDF into an Excel spreadsheet
                  </p>
                </div>
              </div>
            )}

            {/* Conversion Tips */}
            <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h4 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>💡 Conversion Tips</h4>
              <ul className={`text-sm space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>• For best results, use PDFs with clear table structures</li>
                <li>• Enable OCR for scanned documents or image-based PDFs</li>
                <li>• Use AI-enhanced detection for complex table layouts</li>
                <li>• Specify page ranges to convert only relevant sections</li>
                <li>• Preview your settings before conversion</li>
              </ul>
            </div>

            {/* Quick Actions */}
            {selectedFile && (
              <div className="mt-4 flex space-x-2">
                <button
                  className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  onClick={clearAll}
                  type="button"
                >
                  🗑️ Clear All
                </button>
                <button
                  className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  onClick={() => {
                    setOutputFormat('xlsx');
                    setTableDetection('automatic');
                    setQualityLevel('balanced');
                    track('settings_reset');
                  }}
                  type="button"
                >
                  🔄 Reset Settings
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Information Section */}
        <div className={`mt-8 p-6 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>About PDF to Excel Conversion</h2>

          <div className="space-y-4">
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>How This Converter Works</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                This advanced PDF to Excel converter uses sophisticated algorithms to analyze PDF document structure, detect tables, extract data,
                and convert it into properly formatted Excel spreadsheets. The tool supports multiple output formats including XLSX, XLS, CSV,
                ODS, and TSV, making it compatible with Excel, Google Sheets, LibreOffice, and other spreadsheet applications.
              </p>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Advanced Features</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Intelligent Table Detection</strong> - Multiple detection modes including automatic, AI-enhanced, and grid-based algorithms</li>
                <li><strong>OCR Support</strong> - Extract text from scanned PDFs and image-based documents in 13+ languages</li>
                <li><strong>Format Preservation</strong> - Maintain fonts, colors, styles, and cell formatting from original PDF</li>
                <li><strong>Image Extraction</strong> - Handle embedded images with options to embed, extract, or link</li>
                <li><strong>Data Cleanup</strong> - Automatic text cleanup, empty row/column removal, and cell merging</li>
                <li><strong>Multiple Output Formats</strong> - Support for Excel (.xlsx/.xls), CSV, OpenDocument (.ods), and TSV</li>
                <li><strong>Security Features</strong> - Password protection and various compression levels</li>
                <li><strong>Batch Processing</strong> - Convert multiple pages or page ranges in a single operation</li>
                <li><strong>Watermark Removal</strong> - Attempt to remove watermarks and unwanted overlays</li>
                <li><strong>Custom Formatting</strong> - Configurable date formats, number formats, and delimiters</li>
                <li><strong>Image Enhancement</strong> - Brightness, contrast, and rotation adjustments for better OCR</li>
                <li><strong>Sheet Organization</strong> - Flexible sheet naming conventions and data organization</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Table Detection Modes</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Automatic Detection</strong> - Uses multiple algorithms to identify tables automatically</li>
                <li><strong>AI-Enhanced Detection</strong> - Machine learning-powered detection for complex layouts</li>
                <li><strong>Grid-Based Detection</strong> - Focuses on visible grid lines and cell borders</li>
                <li><strong>Text-Based Detection</strong> - Analyzes text alignment and spacing patterns</li>
                <li><strong>Manual Selection</strong> - Allows precise table region specification</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Supported PDF Types</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Native PDFs</strong> - Documents created digitally with selectable text and tables</li>
                <li><strong>Scanned Documents</strong> - Image-based PDFs requiring OCR for text extraction</li>
                <li><strong>Mixed Content</strong> - PDFs containing both native text and scanned images</li>
                <li><strong>Financial Reports</strong> - Balance sheets, income statements, and financial data</li>
                <li><strong>Invoices & Receipts</strong> - Business documents with tabular data</li>
                <li><strong>Research Papers</strong> - Academic documents with data tables and charts</li>
                <li><strong>Government Forms</strong> - Official documents with structured data</li>
                <li><strong>Survey Results</strong> - Data collection forms and questionnaires</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Output Format Options</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Excel (.xlsx)</strong> - Modern Excel format with full feature support and compression</li>
                <li><strong>Excel Legacy (.xls)</strong> - Compatible with older Excel versions (97-2003)</li>
                <li><strong>CSV (.csv)</strong> - Universal comma-separated format for data interchange</li>
                <li><strong>OpenDocument (.ods)</strong> - Open standard format for LibreOffice and other tools</li>
                <li><strong>Tab-Separated (.tsv)</strong> - Alternative delimiter format for data containing commas</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Quality Levels</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Fast</strong> - Quick processing with basic table detection (30-60 seconds)</li>
                <li><strong>Balanced</strong> - Good accuracy with reasonable processing time (1-3 minutes)</li>
                <li><strong>High</strong> - Enhanced accuracy with advanced algorithms (3-5 minutes)</li>
                <li><strong>Ultra</strong> - Maximum quality with all enhancement features (5-10 minutes)</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>OCR Language Support</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                The OCR engine supports 13 languages including English, Spanish, French, German, Italian, Portuguese, Russian,
                Chinese (Simplified/Traditional), Japanese, Korean, Arabic, and Hindi. OCR is particularly useful for scanned
                documents, photographed pages, and image-based PDFs where text cannot be directly extracted.
              </p>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Best Practices</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Use high-quality PDFs with clear text and table structures for best results</li>
                <li>Enable OCR for scanned documents or when text appears as images</li>
                <li>Choose AI-enhanced detection for complex or irregular table layouts</li>
                <li>Specify page ranges to process only relevant sections of large documents</li>
                <li>Use format preservation to maintain original styling and appearance</li>
                <li>Enable image processing adjustments for poorly scanned documents</li>
                <li>Test with a small page range first for large or complex documents</li>
                <li>Consider file size limits and processing time when selecting quality levels</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Common Use Cases</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Financial Analysis</strong> - Convert financial statements, budgets, and reports to Excel for analysis</li>
                <li><strong>Data Migration</strong> - Transfer tabular data from PDF reports to spreadsheet applications</li>
                <li><strong>Invoice Processing</strong> - Extract billing data from PDF invoices for accounting systems</li>
                <li><strong>Research Data</strong> - Convert academic tables and research data to Excel for further analysis</li>
                <li><strong>Government Forms</strong> - Process official documents and forms for data entry</li>
                <li><strong>Survey Results</strong> - Convert questionnaire data to spreadsheets for statistical analysis</li>
                <li><strong>Inventory Management</strong> - Extract product catalogs and inventory lists from PDF documents</li>
                <li><strong>HR Documents</strong> - Process employee data, payroll, and organizational charts</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Technical Specifications</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Input:</strong> PDF files up to 100MB in size</li>
                <li><strong>Output:</strong> Multiple formats (XLSX, XLS, CSV, ODS, TSV)</li>
                <li><strong>Processing:</strong> Client-side conversion for privacy and security</li>
                <li><strong>Encoding:</strong> UTF-8, UTF-16, ASCII, and other character encodings</li>
                <li><strong>Compression:</strong> Configurable compression levels for output files</li>
                <li><strong>Security:</strong> Optional password protection for output files</li>
                <li><strong>Performance:</strong> Multi-threaded processing for faster conversion</li>
                <li><strong>Compatibility:</strong> Works with all modern browsers and operating systems</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with ThemeProvider
const PDFToExcel: React.FC = () => {
  return (
    <ThemeProvider>
      <PDFToExcelContent />
    </ThemeProvider>
  );
};

export default PDFToExcel;